import { AlertTriangle, Trash2, X, RefreshCw, CheckCircle2 } from "lucide-react";

export interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "danger" | "warning" | "primary";
  isLoading?: boolean;
  loadingText?: string;
}

export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirm Deletion",
  description = "Are you sure you want to proceed? This action cannot be undone.",
  confirmText = "Delete",
  cancelText = "Cancel",
  variant = "danger",
  isLoading = false,
  loadingText,
}: ConfirmModalProps) {
  if (!isOpen) return null;

  const isDanger = variant === "danger";

  return (
    <div
      className="fixed inset-0 z-60 flex items-center justify-center bg-black/60 dark:bg-black/70 backdrop-blur-xs p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 shadow-2xl max-w-sm w-full overflow-hidden p-5 space-y-4 animate-in fade-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-stone-100 dark:border-stone-800 pb-3">
          <div
            className={`flex items-center gap-2 ${
              isDanger
                ? "text-rose-600 dark:text-rose-400"
                : variant === "warning"
                ? "text-amber-600 dark:text-amber-400"
                : "text-stone-900 dark:text-stone-100"
            }`}
          >
            {isDanger ? (
              <Trash2 className="w-4 h-4" />
            ) : variant === "warning" ? (
              <AlertTriangle className="w-4 h-4 text-amber-500" />
            ) : (
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            )}
            <h3 className="text-sm font-bold text-stone-900 dark:text-stone-100">
              {title}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 p-1 rounded-lg transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="text-xs text-stone-600 dark:text-stone-400 leading-relaxed">
          {description}
        </p>

        <div className="flex justify-end gap-2 pt-2 border-t border-stone-100 dark:border-stone-800">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-800 text-xs text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 transition"
          >
            {cancelText}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-4 py-1.5 rounded-lg font-semibold text-xs disabled:opacity-50 transition flex items-center gap-1.5 ${
              isDanger
                ? "bg-rose-600 hover:bg-rose-500 text-white"
                : variant === "warning"
                ? "bg-amber-600 hover:bg-amber-500 text-white"
                : "bg-stone-900 hover:bg-stone-800 text-white dark:bg-stone-100 dark:hover:bg-white dark:text-stone-900"
            }`}
          >
            {isLoading && <RefreshCw className="w-3 h-3 animate-spin" />}
            <span>
              {isLoading
                ? loadingText ?? (isDanger ? "Deleting..." : "Confirming...")
                : confirmText}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
